import type {
  BrandSettingsWithLocationStateExclusions,
  LocationsBrandConfig,
  LocationsBrandConfigInput,
} from '../types/location';

/**
 * Reads optional excluded location states from Brand Settings.
 *
 * Expected Contentful field:
 * excludedLocationStates: string[]
 *
 * Example:
 * ['NJ', 'MD']
 */
export function getExcludedLocationStateShorts(
  brandSettings: BrandSettingsWithLocationStateExclusions,
): string[] {
  const excludedLocationStates = brandSettings?.fields?.excludedLocationStates;

  if (!Array.isArray(excludedLocationStates)) {
    return [];
  }

  return Array.from(
    new Set(
      excludedLocationStates
        .filter((state): state is string => typeof state === 'string')
        .map((state) => state.trim().toUpperCase())
        .filter((state) => /^[A-Z]{2}$/.test(state)),
    ),
  );
}

/**
 * Applies optional Brand Settings excluded states to the location brand config.
 *
 * If the field is missing/empty, config is returned unchanged.
 */
export function applyExcludedLocationStatesToConfig(
  config: LocationsBrandConfig,
  brandSettings: BrandSettingsWithLocationStateExclusions,
): LocationsBrandConfig {
  const excludedStateShorts = getExcludedLocationStateShorts(brandSettings);

  if (!excludedStateShorts.length) {
    return config;
  }

  return {
    ...config,
    allowedStateShorts: config.allowedStateShorts.filter(
      (stateShort) => !excludedStateShorts.includes(stateShort.toUpperCase()),
    ),
  };
}

/**
 * Checks whether a state should be excluded for the current brand.
 */
export function isLocationStateExcluded(
  stateShort: string | undefined,
  brandSettings: BrandSettingsWithLocationStateExclusions,
): boolean {
  if (!stateShort) {
    return false;
  }

  return getExcludedLocationStateShorts(brandSettings).includes(stateShort.trim().toUpperCase());
}

/**
 * Returns the brand-specific configuration for the Locations Landing Page,
 * including allowed states, brand name, and map view settings.
 */
export function getLocationsBrandConfig({
  brandCode,
  brandNameCasual,
}: LocationsBrandConfigInput): LocationsBrandConfig {
  const resolvedBrandName = brandNameCasual?.toUpperCase() ?? '';
  const normalizedBrandCode = brandCode?.toUpperCase();

  switch (normalizedBrandCode) {
    case 'HRZ':
      return {
        brandName: resolvedBrandName || 'HORIZON',
        allowedStateShorts: ['DE', 'MD', 'NJ', 'PA'],
        // Used by the /locations landing map.
        // Do not change this for the embedded Google map fallback.
        mapView: {
          center: [-77.3, 40.3],
          scale: 6000,
        },
        // Used by ServiceLocationsInteractive when there is no state/page context.
        serviceLocationsMapView: {
          center: [-76, 40.3],
          zoom: 7,
        },
      };
    case 'GLD':
      return {
        brandName: resolvedBrandName || 'GOLD MEDAL',
        allowedStateShorts: ['NJ', 'NY'],
        mapView: {
          center: [-75.5, 41.0],
          scale: 5200,
        },
        serviceLocationsMapView: {
          center: [-74, 41.0],
          zoom: 7,
        },
      };
    case 'CAS':
      return {
        brandName: resolvedBrandName || 'CASTEEL',
        allowedStateShorts: ['GA'],
        mapView: {
          center: [-83.5, 32.8],
          scale: 4200,
        },
        serviceLocationsMapView: {
          center: [-83.5, 32.8],
          zoom: 7,
        },
      };
    case 'WKS':
      return {
        brandName: resolvedBrandName || 'WEEKS',
        allowedStateShorts: ['TX'],
        mapView: {
          center: [-99.9018, 31.9686],
          scale: 2300,
        },
        serviceLocationsMapView: {
          center: [-95.2, 29.55],
          zoom: 8,
        },
      };
    case 'SAN':
      return {
        brandName: resolvedBrandName || 'SANSONE',
        allowedStateShorts: ['FL'],
        mapView: {
          center: [-84, 27.7],
          scale: 4300,
        },
        serviceLocationsMapView: {
          center: [-80.12, 26.55],
          zoom: 8,
        },
      };
    case 'HCH':
      return {
        brandName: resolvedBrandName || 'HUTCHINSON',
        allowedStateShorts: ['DE', 'NJ', 'PA'],
        mapView: {
          center: [-76.3, 40.05],
          scale: 6200,
        },
        serviceLocationsMapView: {
          center: [-74.5, 39.8],
          zoom: 8,
        },
      };
    case 'FNW':
      return {
        brandName: resolvedBrandName || "FENWICK WAYCHOFF'S",
        allowedStateShorts: ['FL'],
        mapView: {
          center: [-84, 27.6648],
          scale: 4300,
        },
        serviceLocationsMapView: {
          center: [-81.55, 30.25],
          zoom: 9,
        },
      };
    case 'SNL':
      return {
        brandName: resolvedBrandName || 'SNELL',
        allowedStateShorts: ['VA'],
        mapView: {
          center: [-79.5, 37.8],
          scale: 5000,
        },
        serviceLocationsMapView: {
          center: [-77.45, 38.85],
          zoom: 8,
        },
      };
    case 'WMA':
      return {
        brandName: resolvedBrandName || 'WM HENDERSON',
        allowedStateShorts: ['DE', 'MD', 'NJ', 'PA'],
        mapView: {
          center: [-77.3, 40.3],
          scale: 6000,
        },
        serviceLocationsMapView: {
          center: [-75.35, 40.05],
          zoom: 8,
        },
      };
    case 'HND':
      return {
        brandName: resolvedBrandName || 'HURLEY & DAVID',
        allowedStateShorts: ['MA'],
        mapView: {
          center: [-72, 42],
          scale: 10000,
        },
        serviceLocationsMapView: {
          center: [-72.3, 42.25],
          zoom: 8,
        },
      };
    case 'ENS':
      return {
        brandName: resolvedBrandName || 'EANES',
        allowedStateShorts: ['NC'],
        mapView: {
          center: [-79.0, 34.7],
          scale: 4500,
        },
        serviceLocationsMapView: {
          center: [-79.8, 35.8],
          zoom: 8,
        },
      };
    case 'HRP':
      return {
        brandName: resolvedBrandName || 'HARP',
        allowedStateShorts: ['CT', 'MA'],
        mapView: {
          center: [-72, 42],
          scale: 10000,
        },
        serviceLocationsMapView: {
          center: [-72.4, 41.75],
          zoom: 8,
        },
      };
    case 'IER':
      return {
        brandName: resolvedBrandName || "IERNA'S",
        allowedStateShorts: ['FL'],
        mapView: {
          center: [-84, 27.6648],
          scale: 4300,
        },
        serviceLocationsMapView: {
          center: [-82.35, 28.15],
          zoom: 9,
        },
      };
    case 'MTZ':
      return {
        brandName: resolvedBrandName || 'MAITZ',
        allowedStateShorts: ['PA'],
        mapView: {
          center: [-78, 40.2033],
          scale: 6000,
        },
        serviceLocationsMapView: {
          center: [-75.8, 40.6],
          zoom: 8,
        },
      };
    case 'NIC':
      return {
        brandName: resolvedBrandName || 'NICHOLSON',
        allowedStateShorts: ['MA'],
        mapView: {
          center: [-71.8, 42.2],
          scale: 10000,
        },
        serviceLocationsMapView: {
          center: [-71.6, 42.2],
          zoom: 9,
        },
      };
    case 'PFM':
      return {
        brandName: resolvedBrandName || 'PERFORMANCE',
        allowedStateShorts: ['FL'],
        mapView: {
          center: [-84, 27.6648],
          scale: 4300,
        },
        serviceLocationsMapView: {
          center: [-82.45, 27.9],
          zoom: 8,
        },
      };
    case 'ELT':
      return {
        brandName: resolvedBrandName || 'ELITE',
        allowedStateShorts: ['FL'],
        mapView: {
          center: [-84, 27.6648],
          scale: 4300,
        },
        serviceLocationsMapView: {
          center: [-81.5, 27.2],
          zoom: 8,
        },
      };
    case 'GVS':
      return {
        brandName: resolvedBrandName || 'GERVAIS',
        allowedStateShorts: ['MA'],
        mapView: {
          center: [-71.8, 42.2],
          scale: 10000,
        },
        serviceLocationsMapView: {
          center: [-71.82, 42.35],
          zoom: 9,
        },
      };
    default:
      return {
        brandName: resolvedBrandName,
        allowedStateShorts: [],
        mapView: {
          center: [-77.3, 40.3],
          scale: 6000,
        },
        serviceLocationsMapView: {
          center: [-75.35, 40.05],
          zoom: 8,
        },
      };
  }
}
